// Spec 45 component-behavior — cmp_ds_checkbox.
//
// Native <input type="checkbox"> driven; we add data-attrs for styling
// + handle the indeterminate property (which native HTML doesn't expose
// declaratively in attributes). 3 states per spec: unchecked / checked
// / indeterminate, plus disabled / readonly modifiers.
//
// indeterminate is a runtime-only property — set via ref imperatively.
// We expose it as a controlled prop and sync via useLayoutEffect so the
// visual state matches before paint.
//
// Keyboard: Space toggles (native behavior). Enter does NOT toggle (HTML
// spec — that's the form-submit key). The component preserves both.

import { forwardRef, useCallback, useLayoutEffect, useRef, type InputHTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  /** Tri-state: when true, sets the input's `indeterminate` runtime prop. Caller still owns `checked`. */
  readonly indeterminate?: boolean;
  readonly error?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      indeterminate = false,
      error = false,
      disabled,
      className,
      ...rest
    },
    forwardedRef,
  ) => {
    const localRef = useRef<HTMLInputElement>(null);
    const setRef = useCallback(
      (node: HTMLInputElement | null) => {
        localRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef !== null) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    useLayoutEffect(() => {
      const el = localRef.current;
      if (el !== null) {
        el.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    return (
      <input
        ref={setRef}
        type="checkbox"
        data-component="checkbox"
        data-error={error || undefined}
        data-indeterminate={indeterminate || undefined}
        aria-invalid={error || undefined}
        aria-checked={indeterminate ? "mixed" : undefined}
        disabled={disabled === true}
        className={cx("nai-checkbox", className)}
        {...rest}
      />
    );
  },
);

Checkbox.displayName = "Checkbox";
